import React from 'react';
import { Divider, Flex } from '@withjoy/joykit';
import type { AccommodationListingDTO, AccommodationsConsoleState, Filters, RenderListingEntryFn } from '@shared/types/Accommodations.types';
import { ListingEntry } from './components/ListingEntry';
import { CoupleRecommendationsCheck } from './components/CoupleRecommendationsCheck';
import { useAccommodationsListingMobileLayout } from '../../hooks/useAccommodationsListingMobileLayout';
import { SpecialEventRatesTag } from './components/SpecialEventRatesTag';
import DeskTopListingsHeader from './components/DeskTopListingsHeader';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResetFiltersHeader } from './components/ResetFiltersHeader';
import type { SortOption, HotelSearchSource } from '@shared/constants/accommodations';
import { ErrorState } from '../ErrorState';

type ListingsPanelProps = Readonly<{
  hidePanel?: boolean;
  isLoadingPrices: boolean;
  listings: ReadonlyArray<AccommodationListingDTO>;
  state: AccommodationsConsoleState;
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
  noOfNights: number;
  filters: Filters;
  updateFilters: (filters: Filters) => void;
  hasError: boolean;
  sourceName: HotelSearchSource;
  includeCouplesPicks: boolean;
  renderListingEntry: RenderListingEntryFn;
  currencyCode: string;
  navigatorLanguage: string;
  dynamicPriceMax: number;
  showFundSavingsSort?: boolean;
  accentColor?: string;
}>;

const Container: React.FC<{ hide?: boolean; useNewMobileLayout?: boolean }> = ({ hide, children, useNewMobileLayout }) => {
  return (
    <Flex
      height="fit-content"
      flexDirection="column"
      display={hide ? 'none' : 'flex'}
      width={{ _: 'full', md: pxToRem(821) }}
      paddingLeft={useNewMobileLayout ? 4 : 6}
      paddingY={{ _: 6, sm2: 8 }}
      paddingRight={{ _: useNewMobileLayout ? 4 : 6, sm2: 8 }}
      rowGap={{ _: 6, sm2: 6 }}
    >
      {children}
    </Flex>
  );
};

const ListingsPanel = (props: ListingsPanelProps) => {
  const {
    hidePanel,
    listings,
    state,
    noOfNights,
    sortOption,
    onSortChange,
    filters,
    updateFilters,
    hasError,
    sourceName,
    includeCouplesPicks,
    renderListingEntry,
    currencyCode,
    navigatorLanguage,
    dynamicPriceMax,
    showFundSavingsSort,
    accentColor,
  } = props;
  const { isMobileOrTablet, shouldUseNewMobileLayout } = useAccommodationsListingMobileLayout();

  return (
    <Container
      hide={hidePanel}
      useNewMobileLayout={shouldUseNewMobileLayout}
    >
      {isMobileOrTablet ? (
        <SpecialEventRatesTag />
      ) : (
        <DeskTopListingsHeader
          sortOption={sortOption}
          onSortChange={onSortChange}
          includeCouplesPicks={includeCouplesPicks}
          includeFundSavings={showFundSavingsSort}
          accentColor={accentColor}
        />
      )}
      {hasError ? (
        <ErrorState />
      ) : (
        <>
          <ResetFiltersHeader
            filters={filters}
            updateFilters={updateFilters}
            currencyCode={currencyCode}
            navigatorLanguage={navigatorLanguage}
            dynamicPriceMax={dynamicPriceMax}
          />
          {listings.map(listing => (
            <React.Fragment key={listing.id}>
              {renderListingEntry({ listing, state, noOfNights, sourceName })}
              {shouldUseNewMobileLayout && <Divider />}
            </React.Fragment>
          ))}
        </>
      )}
    </Container>
  );
};

ListingsPanel.Skeleton = ({ isMobileOrTablet }: { isMobileOrTablet: boolean }) => {
  return (
    <Container>
      {isMobileOrTablet ? <SpecialEventRatesTag.Skeleton /> : <CoupleRecommendationsCheck.Skeleton />}
      <ListingEntry.Skeleton />
      {isMobileOrTablet && <Divider />}
      <ListingEntry.Skeleton />
      {isMobileOrTablet && <Divider />}
      <ListingEntry.Skeleton />
      {isMobileOrTablet && <Divider />}
      <ListingEntry.Skeleton />
    </Container>
  );
};

export { ListingsPanel };
